import type { CreateUserInput, UserDetail } from '#shared/types';
import type { SetField } from '#client/hooks/useFormValues';

/** Sets one field of the form. */
export type SetUserValue = SetField<UserFormValues>;

/** Create requires a password; edit leaves it blank unless it is being changed. */
export type UserFormValues = Omit<CreateUserInput, 'password'> & { password: string };

function newUserValues(): UserFormValues {
  return {
    first_name: '',
    last_name: '',
    age: 0,
    gender: 'male',
    phone_number: '',
    email: '',
    father_name: '',
    field: '',
    /** 0 until a role is picked; the form refuses to submit without one. */
    role_id: 0,
    photo_url: null,
    designation: '',
    qualifications: '',
    signature_data_url: null,
    password: '',
  };
}

/**
 * The user being edited, or a blank form with no role chosen.
 *
 * The password is never sent to the browser, so there is nothing to prefill.
 */
export function initialUserValues(record: UserDetail | undefined): UserFormValues {
  if (!record) return newUserValues();
  return {
    first_name: record.first_name,
    last_name: record.last_name ?? '',
    age: record.age,
    gender: record.gender,
    phone_number: record.phone_number,
    email: record.email,
    father_name: record.father_name,
    field: record.field,
    role_id: record.role_id,
    photo_url: record.photo_url,
    designation: record.designation ?? '',
    qualifications: record.qualifications ?? '',
    signature_data_url: record.signature_data_url,
    password: '',
  };
}

/**
 * On edit the password is optional; an empty one is stripped so the server
 * keeps the existing hash.
 */
export function toUserPayload(
  values: UserFormValues,
  isEdit: boolean,
): UserFormValues | Omit<UserFormValues, 'password'> {
  if (!isEdit || values.password !== '') return values;
  const { password: _password, ...rest } = values;
  return rest;
}
